import { Button, ButtonBase, Skeleton } from "@mui/material";
import { useTranslations } from "use-intl";

import type { Trip } from "@/app/lib/types/trip";
import { formatCurrency } from "@/app/lib/utils/currency";
import { ImageCarousel } from "./ImageCarousel";
import { TripTag } from "./TripTag";

type Props = {
  trip: Trip;
  onClick: () => void;
};

export const TripCard = ({ trip, onClick }: Props) => {
  const t = useTranslations();

  return (
    <ButtonBase
      onClick={onClick}
      sx={{
        padding: "calc(var(--spacing))",
        backgroundColor: "var(--color-white)",
        borderRadius: "var(--radius-3xl)",
        display: "block",
        width: "100%",
        textAlign: "initial",
      }}
    >
      <ImageCarousel
        title={trip.name}
        subtitle={trip.subtitle}
        imagesUrls={trip.imageUrls}
        tags={trip.tags.map((tag) => (
          <TripTag key={tag} title={tag} />
        ))}
      />

      <div className="flex items-center justify-between gap-2 rounded-3xl p-4">
        <div className="text-text-primary">
          <div className="text-base font-bold">
            {trip.price !== trip.discountedPrice ? (
              <div className="text-sm font-medium text-semantic-error-strong line-through">
                {formatCurrency(trip.price)}
              </div>
            ) : null}
            {t("trips.priceFrom", {
              price: formatCurrency(trip.discountedPrice),
            })}
          </div>
          <div className="text-xs font-medium">
            {t("trips.perPerson")}{" "}
            <span className="text-[#D9D9D9]">&bull;</span>{" "}
            {t("trips.duration", {
              nights: trip.durationInDays - 1,
              days: trip.durationInDays,
            })}
          </div>
        </div>

        <Button
          component="span"
          role={undefined}
          size="small"
          variant="outlined"
          className="shrink-0"
        >
          {t("trips.bookNow")}
        </Button>
      </div>
    </ButtonBase>
  );
};

export const TripCardSkeleton = () => {
  return (
    <div className="rounded-3xl bg-white p-1">
      <div className="flex aspect-7/5 shrink-0 overflow-clip rounded-t-2xl">
        <Skeleton width="100%" height="100%" variant="rectangular" />
      </div>

      <div className="flex items-center justify-between gap-2 p-4">
        <div className="flex grow-1 flex-col gap-1">
          <Skeleton width="50%" height={20} />
          <Skeleton width="70%" height={16} />
        </div>

        <Skeleton
          width={100}
          height={36}
          sx={{ borderRadius: "calc(infinity * 1px)" }}
        />
      </div>
    </div>
  );
};
